import React from 'react';

import { gql } from '@apollo/client';

import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';
import { passwordRule } from '@repo/util/passwordRule';

import { Card } from '@repo/ui/Card';
import { Form } from '@repo/ui/Form';
import { TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

// FORM SCHEMA
const updateUserPasswordFormSchema = z
  .object({
    oldPassword: z.string(),
    password: passwordRule,
    passwordConfirmation: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match. Please try again.',
    path: ['passwordConfirmation'],
  });

type UpdateUserPasswordFormParams = TypeOf<typeof updateUserPasswordFormSchema>;

interface UpdateUserPasswordProps {
  userId: string;
}

const UpdateUserPassword = ({ userId }: UpdateUserPasswordProps) => {
  // Mutation
  const UPDATE_USER_PASSWORD = gql`
    mutation UpdateUserPassword($id: ID!, $input: UpdateUserPasswordInput!) {
      updateUserPassword(id: $id, input: $input) {
        user {
          id
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async (input: UpdateUserPasswordFormParams) => {
    await client.mutate({
      mutation: UPDATE_USER_PASSWORD,
      variables: {
        input,
        id: userId,
      },
    });
    form.reset();
  };
  const form = useZodForm({
    schema: updateUserPasswordFormSchema,
    onSubmit,
  });

  return (
    <>
      <Card title="Update user password">
        <Form {...form}>
          <Form.Success>User password successfully changed.</Form.Success>
          <Form.Error>
            Error while trying to update password. Please try again later or contact support if
            error persists.
          </Form.Error>
          <TextInputField
            type="password"
            label="Current password"
            {...registerInput(form, 'oldPassword')}
          />
          <TextInputField
            type="password"
            label="New password"
            description="Your password must be at least 12 characters long and include a number, a symbol, and a capital letter."
            {...registerInput(form, 'password')}
          />
          <TextInputField
            type="password"
            label="Confirm new password"
            {...registerInput(form, 'passwordConfirmation')}
          />
          <div className="w-full">
            <Button type="submit">Update Password</Button>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default UpdateUserPassword;
